/** Resolved place label for the smart header context row. */
export interface HeaderPlace {
  locality: string;
  city: string;
  label: string;
}

export interface HeaderCoords {
  lat: number;
  lon: number;
}

const CACHE_KEY = 'rm-header-place-v1';
const TTL_MS = 30 * 60 * 1000;

interface PlaceCacheEntry extends HeaderPlace, HeaderCoords {
  fetchedAt: number;
}

function readCache(): PlaceCacheEntry | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PlaceCacheEntry;
  } catch {
    return null;
  }
}

function writeCache(entry: PlaceCacheEntry): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    /* ignore */
  }
}

function formatLabel(locality: string, city: string): string {
  const parts = [locality, city].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : 'Current Location';
}

/** Read cached coords + label if still fresh. */
export function getCachedHeaderPlace(): (HeaderPlace & HeaderCoords) | null {
  const cached = readCache();
  if (!cached) return null;
  if (Date.now() - cached.fetchedAt > TTL_MS) return null;
  return cached;
}

function readCoords(): Promise<HeaderCoords> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('unsupported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      reject,
      { enableHighAccuracy: false, timeout: 12_000, maximumAge: TTL_MS },
    );
  });
}

/** Lightweight reverse geocode (client-side, no Google Maps required). */
async function reverseGeocodeClient(lat: number, lon: number): Promise<HeaderPlace | null> {
  try {
    const params = new URLSearchParams({
      latitude: String(lat),
      longitude: String(lon),
      localityLanguage: 'en',
    });
    const res = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?${params}`,
    );
    if (!res.ok) return null;
    const data = (await res.json()) as {
      locality?: string;
      city?: string;
      principalSubdivision?: string;
    };
    const locality = data.locality?.trim() ?? '';
    const city = (data.city ?? data.principalSubdivision ?? '').trim();
    return {
      locality,
      city,
      label: formatLabel(locality, city),
    };
  } catch {
    return null;
  }
}

/** Resolve GPS coordinates only (no reverse geocode); cached for 30 minutes. */
export async function resolveHeaderCoords(): Promise<HeaderCoords | null> {
  const cached = getCachedHeaderPlace();
  if (cached) return { lat: cached.lat, lon: cached.lon };

  try {
    const { lat, lon } = await readCoords();
    const entry: PlaceCacheEntry = {
      locality: '',
      city: '',
      label: '',
      lat,
      lon,
      fetchedAt: Date.now(),
    };
    writeCache(entry);
    return { lat, lon };
  } catch (err) {
    console.warn('[RideMaadi Header Weather] Geolocation failed', err);
    const stale = readCache();
    if (stale) {
      console.warn('[RideMaadi Header Weather] Using stale cached coordinates');
      return { lat: stale.lat, lon: stale.lon };
    }
    return null;
  }
}

/** Resolve GPS coordinates and locality label; cached for 30 minutes. */
export async function resolveHeaderPlace(): Promise<(HeaderPlace & HeaderCoords) | null> {
  const cached = getCachedHeaderPlace();
  if (cached) return cached;

  try {
    const { lat, lon } = await readCoords();
    const place = (await reverseGeocodeClient(lat, lon)) ?? {
      locality: '',
      city: '',
      label: 'Current Location',
    };
    const entry: PlaceCacheEntry = { ...place, lat, lon, fetchedAt: Date.now() };
    writeCache(entry);
    return entry;
  } catch (err) {
    console.warn('[RideMaadi Header Weather] resolveHeaderPlace failed', err);
    return null;
  }
}

export function clearHeaderPlaceCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
}
